import { SetupInstance } from '../../app/msal-account-helpers';
import { protectedResources } from '../../authConfig';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { useLocal } from './../environment';
import { hydrateObjects, cleanUpJsonForTransport } from '../json-helper';
import polly from 'polly-js';
import { AccessTokenHelper } from '../access-token-helper';
import { arrayAny } from '../../utils/arrayUtils';
import { msalConfig } from '../../app';

export class UserApi {
    constructor(instance, inProgress, accounts, forceRefresh) {

        this.instance = SetupInstance(instance);
        this.inProgress = inProgress;

        // Do not pin to accounts[0]; rely on MSAL active account
        this.accessTokenRequest = {
            scopes: [
                ...protectedResources.apiProperty.scopes.read
            ],
            authority: `https://login.microsoftonline.com/${msalConfig.auth.tenantId}/`,
            forceRefresh: forceRefresh,
            // account is selected by AccessTokenHelper via instance.getActiveAccount()
        };

        this.baseUrl = useLocal ?
            "http://localhost:8086/api/users" :
            "https://apim.ianrobertson.co.uk/api/users/v1";

        this.accessTokenService = new AccessTokenHelper();
    }

    me = async () => {
        const me = await this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest);
        return me;
    };

    hasRole = (userRoles, role) => {
        return !!userRoles && arrayAny(userRoles) ? userRoles.includes(role) : false;
    }

    user = id => polly()
        .logger(error => {
            console.error("An error occurred in fetch user", error); // will be hit 5 times if issues
        })
        .waitAndRetry(5)
        .executeForPromise(() => {
            return new Promise((resolve, reject) => {

                if (this.inProgress !== InteractionStatus.None) {
                    reject("Login interaction in-progress");
                    return;
                }

                this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                    .then(async accessTokenResponse => {

                        if (!!!accessTokenResponse) {
                            return reject("No access token");
                        }

                        const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                        const headers = new Headers();
                        headers.append("Authorization", bearer);
                        headers.append("Content-Type", "application/json");

                        const options = {
                            method: "GET",
                            headers: headers
                        };

                        try {
                            const response = await fetch(`${this.baseUrl}/${id}`, options);
                            
                            if (!response.ok) {
                                return reject(new Error(`HTTP error! Status: ${response.status}`));
                            }
                            
                            const data = await response.json();
                            let result = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
                            
                            if (id === 'all') {
                                return resolve(result);
                            }
                            else {
                                const self = result.self;
                                result = result.user;
                                result.self = self;
                                return resolve(result);
                            }
                        } catch (error) {
                            console.log(error);
                            return reject(error);
                        }
                    })
                    .catch((error) => {
                        if (error instanceof InteractionRequiredAuthError) {
                            // Let centralized auth handling manage redirects
                        }
                        console.log(error);
                        return reject(error);
                    });
            });
        });

    updateUser = user => {

        const userDoc = structuredClone(user);

        return polly()
            .logger(error => {
                console.error("An error occurred in updateUser", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise((resolve, reject) => { // eslint-disable-line no-unused-vars

                    if (this.inProgress !== InteractionStatus.None) {
                        return this.instance.acquireTokenSilent(this.accessTokenRequest);
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const options = {
                                method: "PATCH",
                                headers: headers,
                                body: JSON.stringify(cleanUpJsonForTransport(userDoc))
                            };

                            fetch(`${this.baseUrl}/update`, options)
                                .then(response => response.json())
                                .then(data => {
                                    let result = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
                                    const self = result.self;
                                    result = result.user;
                                    result.self = self;
                                    return resolve(result);
                                })
                                .catch(error => {
                                    console.log(error);
                                    return reject(error)
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error)
                        });
                });
            });

    };

    #transformHeartBeatReport = results => {

        const data = results.map(r => {
            return { time: new Date(r.startTime), active: r.isActive ? 1 : 0 };
        });

        const dayMap = this.#splitDataByDay(data);
        const result = {};

        for (const day in dayMap) {
            result[day] = this.#fillMissingIntervals(dayMap[day]);
        }

        return result;

    };

    #splitDataByDay = (data) => {
        const dayMap = {};

        data.forEach(entry => {
            //const date = new Date(entry.time);
            const day = entry.time.toISOString().split('T')[0]; // Extract the date part

            if (!dayMap[day]) {
                dayMap[day] = [];
            }

            dayMap[day].push(entry);
        });

        return dayMap;
    };

    #fillMissingIntervals = (dayData) => {

        const filledData = [];
        const intervals = 24 * 4; // 24 hours * 4 intervals per hour (15 minutes each)

        const time = dayData[0].time;
        const startDate = new Date(time.getFullYear(), time.getMonth(), time.getDate());

        for (let i = 0; i < intervals; i++) {
            const intervalTime = new Date(startDate.getTime() + i * 15 * 60 * 1000);
            const intervalISO = intervalTime.toISOString().slice(0, -1);

            const existingEntry = dayData.find(entry => entry.time.toISOString().slice(0, -1) === intervalISO);

            filledData.push({ time: intervalISO, active: existingEntry ? 1 : 0 });
        }

        return filledData;
    };

    getHeartBeatReports = (userId, start, end) =>

        polly()
            .logger(error => {
                console.error("An error occurred in getHeartBeatReports", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise((resolve, reject) => { // eslint-disable-line no-unused-vars

                    if (this.inProgress !== InteractionStatus.None) {
                        return this.instance.acquireTokenSilent(this.accessTokenRequest);
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const options = {
                                method: "GET",
                                headers: headers
                            };

                            fetch(`${this.baseUrl}/heartbeats/${userId}?start=${start}&end=${end}`, options)
                                .then(response => response.json())
                                .then(data => {
                                    const result = cleanUpJsonForTransport(Object.assign([], hydrateObjects(data)));
                                    const transformed = this.#transformHeartBeatReport(result);
                                    return resolve(transformed);
                                })
                                .catch(error => {
                                    console.log("An error occurred fetching report", error);
                                    return reject(error)
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error)
                        });
                });
            });

    getApiRoles = () => polly()
        .logger(error => {
            console.error("An error occurred in getApiRoles", error); // will be hit 5 times if issues
        })
        .waitAndRetry(5)
        .executeForPromise(() => {
            return new Promise((resolve, reject) => { // eslint-disable-line no-unused-vars

                if (this.inProgress !== InteractionStatus.None) {
                    return this.instance.acquireTokenSilent(this.accessTokenRequest);
                }

                this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                    .then(accessTokenResponse => {

                        if (!!!accessTokenResponse) {
                            return reject("No access token");
                        }

                        const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                        const headers = new Headers();
                        headers.append("Authorization", bearer);
                        headers.append("Content-Type", "application/json");

                        const options = {
                            method: "GET",
                            headers: headers
                        };

                        fetch(`${this.baseUrl}/GetApiRoles`, options)
                            .then(response => response.json())
                            .then(data => {
                                const result = cleanUpJsonForTransport(Object.assign([], hydrateObjects(data)));
                                return resolve(result);
                            })
                            .catch(error => {
                                console.log(error);
                                return reject(error)
                            });
                    })
                    .catch((error) => {
                        if (error instanceof InteractionRequiredAuthError) {
                            // Let centralized auth handling manage redirects
                        }
                        console.log(error);
                        return reject(error)
                    });
            });
        });            
}